import { Component } from '@theme/component';

/**
 * @typedef {Object} NwTabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<NwTabsRefs>} */
class NwTabsComponent extends Component {
  /** @type {number} */
  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #vertical = false;

  connectedCallback() {
    super.connectedCallback();
    this.#vertical = this.getAttribute('style')?.includes('vertical') ?? false;
    this.#syncActiveFromDom();
    this.#syncTabLabelsFromPanels();
    this.#updateActiveTab();
    this.#setupEventListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncActiveFromDom();
    this.#syncTabLabelsFromPanels();
    this.#updateActiveTab();
  }

  /**
   * Rellena el texto de cada tab desde data-tab-title del panel (el block sí tiene acceso a block.settings).
   */
  #syncTabLabelsFromPanels() {
    const { tabs, panels } = this.refs;
    if (!panels?.length || !tabs?.length) return;
    for (let i = 0; i < panels.length; i++) {
      const panel = panels[i];
      if (!panel) continue;
      const title = (panel.dataset.tabTitle || '').trim();
      const tab = tabs[i];
      if (!tab) continue;

      const label = tab.querySelector('.nw-tabs__tab-label');
      const nextText = title || `Tab ${i + 1}`;

      if (label) {
        label.textContent = nextText;
      } else {
        tab.textContent = nextText;
      }

      const iconSlot = tab.querySelector('.nw-tabs__tab-icon-slot');
      const iconTemplate = panel.querySelector('template[data-tab-icon-template]');
      if (iconSlot) {
        iconSlot.innerHTML = iconTemplate?.innerHTML?.trim() || '';
      }
    }
  }

  #syncActiveFromDom() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    const idx = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.#active = idx >= 0 ? idx : 0;
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;
    if (!tabs) return;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);
    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
    }
  }

  /**
   * @param {KeyboardEvent} e
   */
  #handleKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(target);
    const prevKey = this.#vertical ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = this.#vertical ? 'ArrowDown' : 'ArrowRight';
    let nextIndex = -1;

    if (e.key === prevKey) {
      e.preventDefault();
      nextIndex = i - 1;
    } else if (e.key === nextKey) {
      e.preventDefault();
      nextIndex = i + 1;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = tabs.length - 1;
    }

    if (nextIndex >= 0 && nextIndex < tabs.length) {
      this.#activate(nextIndex);
      tabs[nextIndex]?.focus();
    }
  }

  /**
   * @param {MouseEvent} e
   * @param {number} index
   */
  #handleTabClick(e, index) {
    e.preventDefault();
    this.#activate(index);
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;
    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs?.entries() ?? []) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      const isActive = i === this.#active;
      panel.setAttribute('aria-hidden', String(!isActive));
      panel.toggleAttribute('inert', !isActive);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }
}

customElements.define('nw-tabs-component', NwTabsComponent);
